import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Tooltip, Tag, InputNumber, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
// TIMEZONE
import moment from "moment-timezone";
// REDUX
import { useSelector } from "react-redux";
import { usePostFaultRecentReportMutation } from "../../../redux/reports/reportsAPI";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
import {
  buildLiftFaultRequestBody,
  buildLiftFaultsData,
  formatFaultDuration,
} from "../../../utils/buildLiftFaultsData";
// FLATBUFFER DISPLAY HELPERS
import { formatUuidT } from "../../../flatbuffers/display";

export default function ControlPanelLiftFaults() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [faults, setFaults] = useState<any>([]);
  const [weeks, setWeeks] = useState<number>(3);
  const [limit, setLimit] = useState<number>(30);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { liftSelected } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetReport, { data: data2, isLoading: isLoading2 }] =
    usePostFaultRecentReportMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getFaultMessages = async () => {
    const token: string = await GETJwtToken();
    triggerGetReport({
      token: token || "",
      body: buildLiftFaultRequestBody(liftSelected, moment.tz.guess(), weeks, limit),
    });
  };

  const buildReportsData = (response: any) => {
    setFaults(buildLiftFaultsData(response));
  };

  const getHeaderFaultsDate = () => {
    const end = moment(); // hoy
    const start = moment().subtract(weeks, "weeks");
    return (
      <div style={{ width: "100%", padding: "10px" }}>
        {/* Row 1: Date range */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Tag color="blue" style={{ width: "150px", textAlign: "center" }}>
            {start.format("MMM D, YYYY")}
          </Tag>
          <span
            style={{
              fontSize: "12px",
              opacity: 0.65,
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            to
          </span>
          <Tag color="blue" style={{ width: "150px", textAlign: "center" }}>
            {end.format("MMM D, YYYY")}
          </Tag>
        </div>
        {/* Row 2: Filters */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            marginTop: "10px",
          }}
        >
          <span style={{ fontSize: "12px", opacity: 0.75 }}>Weeks:</span>
          <InputNumber
            min={1}
            max={12}
            value={weeks}
            size="small"
            style={{ width: "70px" }}
            onChange={(val) => setWeeks(val || 3)}
          />
          <span style={{ fontSize: "12px", opacity: 0.75 }}>Limit:</span>
          <InputNumber
            min={1}
            max={500}
            value={limit}
            size="small"
            style={{ width: "70px" }}
            onChange={(val) => setLimit(val || 30)}
          />
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            loading={isLoading2}
            onClick={() => getFaultMessages()}
          >
            Search
          </Button>
        </div>
      </div>
    );
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (liftSelected && liftSelected.id) {
      getFaultMessages();
    }
  }, [liftSelected]);

  useEffect(() => {
    if (data2 && data2.results) {
      buildReportsData(data2.results);
    }
  }, [data2]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="monitoringView__detailsBox" style={{ minHeight: "400px", height: "auto" }}>
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
          height: "auto",
          paddingBottom: "25px",
        }}
      >
        {/** FAULTS */}
        <Table
          rowClassName={(record, index) => getTableRowClass(index, theme)}
          className="my-custom-table"
          dataSource={faults}
          loading={isLoading2}
          rowKey={(record: any) => formatUuidT(record.id)}
          size="small"
          title={() => getHeaderFaultsDate()}
        >
          {/** Index */}
          <Column
            title="No"
            width="50px"
            render={(text, record: any, index: number) => (
              <>
                <div className="generalStyles__info">{index + 1}</div>
              </>
            )}
          />
          {/** Car name */}
          <Column
            title="Unit"
            dataIndex="name"
            key="name"
            render={(text) => (
              <Tooltip placement="top" title={text}>
                <div>{text} </div>{" "}
              </Tooltip>
            )}
          />
          {/** Created at */}
          <Column
            title="Created at"
            dataIndex="created_at"
            key="created_at"
            render={(text) => (
              <Tooltip placement="top" title={text}>
                <div>{text} </div>{" "}
              </Tooltip>
            )}
          />
          {/** Short MSG */}
          <Column
            title="Message"
            dataIndex="short_msg"
            key="short_msg"
            render={(text) => (
              <Tooltip placement="top" title={text || "--"}>
                <div>{text || "--"}</div>
              </Tooltip>
            )}
          />
          {/** Duration */}
          <Column
            title="Duration"
            dataIndex="duration"
            key="duration"
            render={(text, record: any) => {
              const display = formatFaultDuration(text, record.cleared, record.created_at);
              return (
                <Tooltip placement="top" title={display}>
                  <div>{display}</div>
                </Tooltip>
              );
            }}
          />
        </Table>
      </div>
    </div>
  );
}
